import type { KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

/**
 * Abas (padrão ARIA): setas, Home e End trocam de aba; só a aba atual entra no Tab. Cada painel usa
 * `tabPanelProps(id, key)` para se ligar à aba.
 */
export function TabList<T extends string>({
  id,
  label,
  tabs,
  value,
  onChange,
}: {
  id: string;
  label: string;
  tabs: { key: T; label: string }[];
  value: T;
  onChange: (tab: T) => void;
}) {
  const keyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.key === value);
    const next =
      e.key === 'ArrowRight'
        ? tabs[(i + 1) % tabs.length]
        : e.key === 'ArrowLeft'
          ? tabs[(i - 1 + tabs.length) % tabs.length]
          : e.key === 'Home'
            ? tabs[0]
            : e.key === 'End'
              ? tabs[tabs.length - 1]
              : undefined;
    if (!next) return;
    e.preventDefault();
    onChange(next.key);
    document.getElementById(`${id}-tab-${next.key}`)?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex gap-1 overflow-x-auto border-b border-border"
      onKeyDown={keyDown}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          id={`${id}-tab-${t.key}`}
          aria-selected={value === t.key}
          aria-controls={`${id}-${t.key}`}
          tabIndex={value === t.key ? 0 : -1}
          onClick={() => onChange(t.key)}
          className={cn(
            '-mb-px min-h-11 shrink-0 border-b-2 px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring',
            value === t.key
              ? 'border-brand text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export const tabPanelProps = (id: string, key: string) => ({
  role: 'tabpanel' as const,
  id: `${id}-${key}`,
  'aria-labelledby': `${id}-tab-${key}`,
});
